'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const featurePills = [
  { label: 'Based on UK Driving Guidance' },
  { label: '50 Practice Questions' },
  { label: 'Pass Mark 43/50' },
  { label: 'Instant Explanations' },
];

export default function HeroSection() {
  const headlineRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const el = headlineRef?.current;
    if (el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      requestAnimationFrame(() => {
        el.style.transition = 'opacity 0.9s ease, transform 0.9s ease';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      });
    }
  }, []);

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      aria-labelledby="hero-heading"
    >
      {/* ── Background image ── */}
      <div className="absolute inset-0 z-0">
        {/* Mobile background (shown below sm) */}
        <Image
          src="/assets/images/Phone_Hero_Section__1_-1780382080686.jpg"
          alt="Galaxy Driving Academy — mobile hero background"
          fill
          className="object-cover object-center sm:hidden"
          priority
          quality={85}
        />
        {/* Desktop background (shown sm and above) */}
        <Image
          src="/assets/images/Untitled_design__3_-1780380253982.jpg"
          alt="Galaxy Driving Academy — dusk cityscape with Galaxy-branded SUV"
          fill
          className="object-cover object-center hidden sm:block"
          priority
          quality={85}
        />
        {/* Soft dark overlay for text readability */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(110deg, rgba(5,10,24,0.82) 0%, rgba(8,14,32,0.72) 45%, rgba(8,14,32,0.40) 75%, rgba(8,14,32,0.15) 100%)',
          }}
        />
        {/* Bottom fade */}
        <div
          className="absolute bottom-0 left-0 right-0 h-28"
          style={{
            background: 'linear-gradient(to bottom, transparent 0%, rgba(248,249,251,0.92) 100%)',
          }}
        />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-5 sm:px-10 lg:px-16 xl:px-20 pt-36 pb-28 lg:pt-48 lg:pb-40">

        <div className="flex flex-col" style={{ maxWidth: 'min(640px, 94vw)' }}>

          {/* ── Small badge ── */}
          <div className="fade-in-up mb-5 sm:mb-7" style={{ animationDelay: '0.1s', opacity: 0 }}>
            <span
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase"
              style={{
                background: 'rgba(220,38,38,0.14)',
                border: '1px solid rgba(220,38,38,0.30)',
                color: '#FCA5A5',
                letterSpacing: '0.06em',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse-slow flex-shrink-0" />
              UK Theory Test Practice
            </span>
          </div>

          {/* ── Main headline ── */}
          <div className="fade-in-up mb-5 sm:mb-7" style={{ animationDelay: '0.2s', opacity: 0 }}>
            <h1
              ref={headlineRef}
              id="hero-heading"
              className="font-extrabold tracking-tight text-white"
              style={{
                lineHeight: 1.1,
              }}
            >
              <span
                style={{
                  display: 'block',
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.9rem)',
                  fontWeight: 300,
                  letterSpacing: '0.01em',
                  lineHeight: 1.2,
                  marginBottom: '0.15em',
                  opacity: 0.85,
                }}
              >
                Pass Your
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 'clamp(2.8rem, 7vw, 5.2rem)',
                  fontWeight: 900,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.0,
                }}
              >
                <span className="text-gradient-hero">Theory</span> Test
              </span>
            </h1>
          </div>

          {/* ── Supporting text ── */}
          <div className="fade-in-up mb-8 sm:mb-10" style={{ animationDelay: '0.3s', opacity: 0 }}>
            <p
              className="text-sm sm:text-base leading-relaxed"
              style={{ color: 'rgba(203,213,225,0.88)', maxWidth: '500px' }}
            >
              Practice with 50 questions inspired by official UK driving guidance.
              <br />
              <span style={{ color: 'rgba(148,163,184,0.75)', fontSize: '0.85em' }}>
                Not an official DVSA exam.
              </span>
            </p>
          </div>

          {/* ── Feature badges ── */}
          <div className="fade-in-up mb-9 sm:mb-11" style={{ animationDelay: '0.4s', opacity: 0 }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {featurePills?.map((pill) => (
                <div
                  key={`pill-${pill?.label}`}
                  className="flex items-center justify-center gap-1 px-2 py-0 rounded-lg text-center"
                  style={{
                    background: 'rgba(15,23,42,0.40)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    color: 'rgba(186,230,253,0.88)',
                    fontSize: '0.68rem',
                    fontWeight: 500,
                    lineHeight: 1.3,
                    height: '36px',
                    minHeight: '36px',
                    maxHeight: '36px',
                  }}
                >
                  <span className="text-emerald-400 flex-shrink-0" style={{ fontSize: '0.65rem', lineHeight: 1 }}>✓</span>
                  <span style={{ whiteSpace: pill?.label === 'Based on UK Driving Guidance' ? 'normal' : 'nowrap' }}>
                    {pill?.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── CTA Button ── */}
          <div
            className="fade-in-up"
            style={{ animationDelay: '0.5s', opacity: 0 }}
          >
            <Link
              href="/quiz-screen"
              className="btn-primary inline-flex items-center gap-2.5 text-sm sm:text-base px-7 py-3.5 rounded-xl"
              style={{ boxShadow: '0 4px 20px rgba(30,64,175,0.45)' }}
              aria-label="Start free practice quiz"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 7l5 3-5 3V7z" fill="currentColor" />
              </svg>
              Start Free Practice Quiz
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
}